import type { AuthResponse, DriveFile, TrashItem, User } from '../domain/models';
import { API_URL, api, jsonBody } from './client';

export const storageApi = {
  login: (email: string, password: string) => api<AuthResponse>('/auth/login', { method: 'POST', body: jsonBody({ email, password }) }),
  register: (name: string, email: string, password: string) => api<AuthResponse>('/auth/register', { method: 'POST', body: jsonBody({ name, email, password }) }),
  googleStartUrl: () => `${API_URL}/auth/google/start?return_to=${encodeURIComponent(window.location.origin)}`,
  exchangeGoogle: (code: string, password?: string) => api<AuthResponse>('/auth/google/exchange', { method: 'POST', body: jsonBody({ code, password }) }),
  me: () => api<{ user: User }>('/me'),
  listFiles: (parentId: string | null) => api<{ items: DriveFile[] }>(`/files${parentId ? `?parent_id=${encodeURIComponent(parentId)}` : ''}`),
  getFile: (id: string) => api<{ file: DriveFile }>(`/files/${id}`),
  createFolder: (name: string, parentId: string | null) => api<{ file: DriveFile }>('/folders', { method: 'POST', body: jsonBody({ name, parent_id: parentId }) }),
  renameFile: (id: string, name: string) => api<{ file: DriveFile }>(`/files/${id}`, { method: 'PATCH', body: jsonBody({ name }) }),
  trashFile: (id: string) => api(`/files/${id}`, { method: 'DELETE' }),
  listTrash: () => api<{ items: TrashItem[] }>('/trash'),
  restore: (id: string) => api(`/trash/${id}/restore`, { method: 'POST' }),
  permanentlyDelete: (id: string) => api(`/trash/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: string) => api<{ url: string; filename: string }>(`/files/${id}/download-url`),
  initiateUpload: (file: File, parentId: string | null) => api<{ file_id: string; url: string }>('/files/upload-url', {
    method: 'POST',
    body: jsonBody({ name: file.name, parent_id: parentId, size_bytes: file.size, mime_type: file.type || 'application/octet-stream' }),
  }),
  completeUpload: (id: string) => api<{ file: DriveFile }>(`/files/${id}/complete`, { method: 'POST' }),
  initiatePublicUpload: (file: File) => api<{ upload_id: string; upload_token: string; url: string; max_size_bytes: number }>('/public/uploads', {
    method: 'POST',
    body: jsonBody({ name: file.name, size_bytes: file.size, mime_type: file.type || 'application/octet-stream' }),
  }),
  completePublicUpload: (id: string, token: string) => api<{ uploaded: true }>(`/public/uploads/${id}/complete`, {
    method: 'POST',
    body: jsonBody({ upload_token: token }),
  }),
};

export async function uploadToR2(url: string, file: File, onProgress: (progress: number) => void, lowMemory = false): Promise<void> {
  if (lowMemory) {
    onProgress(1);
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new Error('La subida a R2 fue interrumpida.');
    }
    if (!response.ok) throw new Error(`R2 respondió ${response.status}.`);
    onProgress(100);
    return;
  }

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    request.upload.onprogress = (event) => event.lengthComputable && onProgress(Math.round((event.loaded / event.total) * 100));
    request.onerror = () => reject(new Error('La subida a R2 fue interrumpida.'));
    request.onload = () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`R2 respondió ${request.status}.`));
    request.send(file);
  });
}
